import React, { useState, useRef } from "react";
import { engine, VOICE_ORDER, VOICE_LABELS } from "@/lib/drumAudio";
import PatternBuilder from "@/components/PatternBuilder";
import { Grid3x3, Drum } from "lucide-react";

const PAD_COLORS = {
  CC: "#FF3366",
  RD: "#FF8A00",
  HH: "#FFD400",
  HT: "#00F0FF",
  MT: "#00C2FF",
  FT: "#4D7CFF",
  SD: "#F0F6FC",
  BD: "#A855F7",
  CB: "#34D399",
  TM: "#A3E635",
  CP: "#F472B6",
  RS: "#2DD4BF",
  RB: "#FDE047",
  "2BD": "#C084FC",
};

const PADS = [
  ...VOICE_ORDER.map((v) => ({ key: v, sound: v, label: VOICE_LABELS[v] })),
  { key: "2BD", sound: "BD", label: "Double Bass" },
];

export default function VirtualDrumKit({ onLoadTab, isPro, onUpgrade, onSaved }) {
  const [flash, setFlash] = useState({});
  const [mode, setMode] = useState("kit");
  const timers = useRef({});

  const hit = (pad) => {
    engine.ensure();
    if (pad.key === "2BD") {
      engine.trigger("BD", null, true);
      engine.trigger("BD", engine.now + 0.09, true);
    } else {
      engine.trigger(pad.sound, null, true);
    }
    setFlash((f) => ({ ...f, [pad.key]: Date.now() }));
    clearTimeout(timers.current[pad.key]);
    timers.current[pad.key] = setTimeout(
      () => setFlash((f) => ({ ...f, [pad.key]: 0 })),
      180
    );
  };

  return (
    <div className="rounded-2xl bg-[#12161f] border border-white/10 p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-xl uppercase tracking-tight">
          {mode === "kit" ? "Virtual Kit" : "Pattern Builder"}
        </h3>
        <div className="flex rounded-lg border border-white/10 overflow-hidden" data-testid="kit-mode-toggle">
          <button
            onClick={() => setMode("kit")}
            data-testid="kit-mode-kit"
            className={`px-2.5 py-1.5 text-xs font-tab flex items-center gap-1 transition-colors ${mode === "kit" ? "bg-[#00f0ff] text-black" : "text-slate-400 hover:text-white"}`}
          >
            <Drum className="w-3.5 h-3.5" /> Kit
          </button>
          <button
            onClick={() => setMode("build")}
            data-testid="kit-mode-build"
            className={`px-2.5 py-1.5 text-xs font-tab flex items-center gap-1 transition-colors ${mode === "build" ? "bg-[#ffab00] text-black" : "text-slate-400 hover:text-white"}`}
          >
            <Grid3x3 className="w-3.5 h-3.5" /> Build
          </button>
        </div>
      </div>

      {mode === "kit" ? (
        <>
          <p className="text-xs text-slate-500 mb-5 font-tab">Tap a pad to play the sound</p>
          <div className="grid grid-cols-4 gap-3">
            {PADS.map((pad) => {
              const active = !!flash[pad.key];
              const color = PAD_COLORS[pad.key];
              return (
                <button
                  key={pad.key}
                  onClick={() => hit(pad)}
                  data-testid={`virtual-drum-pad-${pad.key}`}
                  className={`relative aspect-square rounded-xl border transition-colors flex flex-col items-center justify-center gap-1 select-none ${
                    active ? "pad-flash" : ""
                  }`}
                  style={{
                    borderColor: active ? color : "rgba(255,255,255,0.1)",
                    background: active ? `${color}22` : "rgba(255,255,255,0.03)",
                    boxShadow: active ? `0 0 20px ${color}66` : "none",
                  }}
                >
                  <span
                    className="font-heading text-lg font-bold"
                    style={{ color: active ? color : "#F0F6FC" }}
                  >
                    {pad.key}
                  </span>
                  <span className="text-[10px] text-slate-500 font-tab text-center leading-tight">{pad.label}</span>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <>
          <p className="text-xs text-slate-500 mb-4 font-tab">Orchestrate your own groove — tap cells or record live</p>
          <PatternBuilder onLoadTab={onLoadTab} isPro={isPro} onUpgrade={onUpgrade} onSaved={onSaved} />
        </>
      )}
    </div>
  );
}
